import _ from 'lodash';
import join from 'url-join';
import uri from 'urijs';
import { MINUTE, HOUR } from 'Utils/time';
import angular from 'angular';
/**
 * These are the fields retrieved for various equipment entities
 * We use these in order to reduce loading unnecessary information
 * which in turn can negatively impact app performance
 */
const SELECTS = {
    dashboard: [
        'name', 'settings.latitude', 'settings.longitude', 'settings.color', 'equipmentType', 'rcu', 'site', 'org'
    ],
    admin_equipment: [
        'name', 'settings.protocol', 'settings.externalIp', 'settings.externalPort', 'settings.ip', 'settings.httpAlt',
        'settings.latitude', 'settings.longitude', 'settings.timezone', 'settings.continuousVideo', 'settings.color',
        'equipmentType', 'disable', 'rcu', 'org', 'site'
    ],
    admin_site: ['name', 'equipmentType', 'site'],
    equipment_management: [
        'name', 'settings.port', 'settings.ip', 'settings.protocol', 'settings.externalIp', 'settings.externalPort',
        'settings.timezone', 'settings.latitude', 'settings.longitude', 'settings.continuousVideo', 'settings.jobs',
        'settings.nvr', 'settings.rtspStream', 'settings.doorName', 'settings.classify', 'settings.aiModels',
        'settings.mmc', 'settings.iccid', 'settings.macAddress', 'settings.serial', 'settings.hwVersion',
        'settings.battery', 'settings.color', 'equipmentType', 'disable', 'rcu', 'groups', 'site', 'org',
        'settings.aiModels', 'createdAt', 'updatedAt'
    ],
    reports: [
        'name', 'equipmentType', 'rcu', 'site', 'org', 'settings.jobs'
    ]
};
const POPULATES = {
    dashboard: ['dashboards'],
    admin_equipment: [''],
    admin_site: [''],
    equipment_management: ['groups', 'site'],
    reports: []
};
/* @ngInject */
export default class EquipmentInfoService {
    currentlyPopulatedWith;
    model;
    need = 'dashboard';
    pendingRequest;
    $q;
    $sails;
    $log;
    userInfoService;
    getEquipmentByTypes = this.getEquipmentByType;
    constructor($q, $sails, $log, UserInfoService) {
        this.$q = $q;
        this.$sails = $sails;
        this.$log = $log;
        this.userInfoService = UserInfoService;
        this.model = {
            byId: {},
            byRCU: {},
            array: [],
            byType: {}
        };
    }
    getEquipment(id) {
        return this.model.byId[id];
    }
    getEquipmentModelObject() {
        return this.model;
    }
    setNeed(need) {
        this.need = need;
    }
    async getEquipmentFromServer() {
        const { need, pendingRequest: { need: pendingNeed } = {} } = this;
        if (this.pendingRequest) {
            const isDuplicateRequest = pendingNeed === need;
            const isRedundantRequest = () => pendingNeed && _.isEmpty(_.difference(SELECTS[need], SELECTS[pendingNeed]));
            if (isDuplicateRequest || isRedundantRequest()) {
                return this.pendingRequest;
            }
        }
        if (this.currentlyPopulatedWith === need) {
            return true;
        }
        const select = (SELECTS[need] || SELECTS.dashboard).map((value) => `select=${value}`);
        const populate = (POPULATES[need] || []).map((value) => `populate=${value}`);
        const query = [...select, ...populate].join('&');
        const pendingRequest = this.$sails.get(`/equipment?${query}`)
            .then(({ data: eqs }) => {
            // using a for each to create the three structures in one single loop,
            // instead of having three functions _.merge, _.values, _.groupBy
            _.forEach(eqs, eq => this.updateEquipment(eq));
            // UX-wise, this won't always work since a request may be skipped if service.need check passes
            // TODO: This handle should be performed by an event listener for deleted equipment
            const zombieEquipment = _.difference(_.keys(this.model.byId), _.map(eqs, 'id'));
            zombieEquipment.forEach((id) => {
                let index;
                // remove from property `array`
                index = _.findIndex(this.model.array, { id });
                if (index > -1) { // only splice array when item is found
                    this.model.array.splice(index, 1); // 2nd parameter means remove one item only
                }
                // remove from property `byType`
                const eqType = this.model.byId[id].equipmentType;
                index = _.findIndex(this.model.byType[eqType], { id });
                if (index > -1) {
                    this.model.byType[eqType].splice(index, 1);
                }
                // remove from property `byRCU`
                const rcu = this.model.byId[id].rcu;
                index = _.findIndex(this.model.byRCU[rcu], { id });
                if (index > -1) {
                    this.model.byRCU[rcu].splice(index, 1);
                }
                delete this.model.byId[id];
            });
            this.currentlyPopulatedWith = need;
        })
            .catch((error) => {
            this.$log.error(`Error: ${error}`);
            this.$q.reject(`Could not get equipment: ${error}`);
        })
            .finally(() => {
            this.pendingRequest = undefined;
        }) // TODO: wtf
        ;
        pendingRequest.need = need;
        return this.pendingRequest = pendingRequest;
    }
    async get(id) {
        if (!id || (Array.isArray(id) && _.compact(id).length === 0)) {
            throw new TypeError('Called get with no arguments');
        }
        id = _.castArray(id);
        const { need = 'dashboard' } = this;
        const URI = uri('/equipment').query({
            select: SELECTS[need],
            populate: POPULATES[need],
            id
        });
        return this.$sails
            .get(URI.toString())
            .then(({ data: eqs }) => {
            eqs.forEach(this.updateEquipment, this);
            return eqs;
        })
            .catch((error) => {
            this.$log.error('Error: ', error);
            return this.$q.reject(error.data);
        });
    }
    /**
     * Getter for equipment with a type filter
     *
     * @param {string|Array<string>} types
     * @returns {Promise<Array<object>>} Equipment
     */
    async getEquipmentByType(types) {
        types = _.castArray(types);
        await this.getEquipmentFromServer();
        const equipment = this.model.byType; // Assign first so it isn't reconstructed on each iteration
        return _.flatten(types.map(type => equipment[type] || []));
    }
    /**
     * Gets equipment based on Id
     * If a string is passed in, it assumes it's an ID and returns only one equipment
     * If it's an array, it assumes it's an array of ids and returns all the matched equipment
     *
     * @param {string|Array<string>} ids
     * @returns {Promise<object | Array<object>>} Equipment
     */
    async getEquipmentById(ids) {
        if (_.isUndefined(ids)) {
            throw new TypeError('Missing id(s) arg. Use getAllEquipment method instead');
        }
        else if (Array.isArray(ids) && _.compact(ids).length === 0) {
            return [];
        }
        const needToFetch = _.reject(_.castArray(ids), id => Boolean(this.model.byId[id]));
        if (needToFetch.length > 0) {
            try {
                await this.get(needToFetch);
            }
            catch { /* ? Permitting 404s */ }
        }
        // TODO: Extend this check to validate need; not needed *yet*
        const equipment = _.values(_.pick(this.model.byId, ids));
        return _.isString(ids) ? _.first(equipment) : equipment;
    }
    async _getFullHelper(ids) {
        // TODO: Should perform a single API request instead but is currently unsupported
        // ? Using allSettled here lets us discard 404s instead of rejecting the entire batch (like .all would)
        return Promise
            .allSettled(ids.map(async (id) => {
            const { data: equipment } = await this.$sails.get(`/equipment/${id}`);
            return equipment;
        }))
            .then((fetches) => {
            fetches.forEach(fetch => fetch.status === 'fulfilled' &&
                fetch.value &&
                this.updateEquipment({ __full: true, ...fetch.value }));
        });
    }
    /**
     * Get fresh objects of equipment. Each object also contains more attributes
     * than what `/equipment` returns for each equipment
     *
     * @param {string|Array<string>} ids
     * @returns {Promise<Array<object>>}
     */
    async getFreshEquipment(ids) {
        await this._getFullHelper(_.castArray(ids));
        return this.getEquipmentById(ids);
    }
    async getFullEquipment(ids) {
        const needToGet = _.reject(_.castArray(ids), id => _.get(this.model, `byId.${id}.__full`));
        await this._getFullHelper(needToGet);
        return this.getEquipmentById(ids);
    }
    /**
     * @param {ListIterator<Equipment, boolean> | IterateeShorthand<Equipment>} [predicate] Passed to filter
     * @returns {Array<object>}
     */
    async getAllEquipments(predicate) {
        await this.getEquipmentFromServer();
        // Must branch here because filter(arr, undefined) will still return a new arr ref
        // which would make getAllEquipments() === getAllEquipments() -> false
        return predicate ?
            _.filter(this.model.array, predicate) :
            this.model.array;
    }
    /**
     * @param {Equipment} equipment
     * @returns {Equipment}
     */
    addEquipment(equipment) {
        return this.$sails
            .post('/equipment/', equipment)
            // TODO: API response workaround for service `need`
            .then(({ data: { id } }) => this.getFreshEquipment(id));
    }
    /**
     * @param {Equipment} equipment
     * @returns {Equipment}
     */
    editEquipment(equipment) {
        return this.$sails[this.userInfoService.isCurrentUserPartner() ? 'put' : 'patch'](join('/equipment', equipment.id), { updates: equipment })
            // TODO: API response workaround for service `need`
            .then(({ data: [{ id }] }) => this.getFreshEquipment(id));
    }
    /**
     * @property {string|number} lng
     * @typedef {object} Coordinate
     * @property {string|number} lat
     */
    /**
     * @param {string} id Equipment
     * @returns {Coordinate} Location
     * @throws If 404
     */
    getLocation(id) {
        return this.$sails.get(`/equipment/location/${id}`).then(({ data: location }) => location);
    }
    /**
     * Update the local data of an equipment object without requesting all pieces of equipments
     *
     * @param {Equipment} equipment
     * @returns {Equipment} model reference
     */
    updateEquipment(equipment) {
        const { id, equipmentType, rcu, __full } = equipment;
        const isNewEq = !this.model.byId[id];
        if (isNewEq) {
            // don't use equipment here so that we don't keep a reference to the local variable equipment
            // instead create a new "ghost" version of it
            _.merge(this.model.byId, { [id]: equipment });
            this.model.array.push(this.model.byId[id]);
            // Add byType
            if (!_.isArray(this.model.byType[equipmentType])) {
                this.model.byType[equipmentType] = [];
            }
            this.model.byType[equipmentType].push(this.model.byId[id]);
            // Add byRCU
            if (!_.isArray(this.model.byRCU[rcu])) {
                this.model.byRCU[rcu] = [];
            }
            this.model.byRCU[rcu].push(this.model.byId[id]);
        }
        else if (__full) {
            angular.copy(equipment, this.model.byId[id]);
        }
        else {
            _.merge(this.model.byId, { [id]: equipment });
        }
        return this.model.byId[id];
    }
    /**
     * Determine how many hours remain for a disabled equipment
     *
     * @param {Equipment} equipment
     */
    getHoursRemaining(equipment) {
        if (equipment.disable) {
            let hoursRemaining = '00:00';
            let status = false;
            let expireDate;
            let disableTime = 0;
            let deltaTime = 0;
            disableTime = Number(equipment.disable.disableTime) || 0;
            expireDate = new Date(equipment.disable.expireDate);
            deltaTime = Number(expireDate) - Date.now();
            if (deltaTime > 0) {
                const hours = Math.trunc(deltaTime / HOUR);
                const minutes = Math.trunc((deltaTime % HOUR) / MINUTE);
                hoursRemaining = `${_.padStart(hours.toString(), 2, '0')}:${_.padStart(minutes.toString(), 2, '0')}`;
                status = true;
            }
            else {
                expireDate = new Date();
            }
            equipment.hoursRemaining = hoursRemaining;
            equipment.deltaTime = deltaTime;
            equipment.disable = {
                status,
                disableTime,
                expireDate
            };
        }
    }
    async subscribeToGivenEquipment(equipment, attempts = 1) {
        const subscribe = (eqs) => this.$sails.post('/equipment/subscribe', { equipment: eqs });
        /* eslint-disable no-await-in-loop, @typescript-eslint/no-loop-func */
        try {
            let attempt = 0;
            let result = await subscribe(equipment);
            while (attempt < attempts && _.get(result, 'data.difference.length', -1) !== 0) { // retry
                console.warn(`Subscription #${attempt} to equipment was unsuccessful… retrying`);
                await new Promise(resolve => setTimeout(resolve, ++attempt * 1000));
                result = await subscribe(equipment);
            }
            attempt = 0;
            return _.get(result, 'data');
        }
        catch (err) {
            console.error(`Cannot subscribe to equipment ${equipment}`, err);
            return null;
        }
        /* eslint-enable no-await-in-loop, @typescript-eslint/no-loop-func */
    }
    unsubscribeToGivenEquipment(equipment) {
        return this.$sails.post('/equipment/unsubscribe', { equipment });
    }
    async getAllTypes() {
        await this.getEquipmentFromServer();
        return Object.keys(this.model.byType);
    }
    /**
     * Get equipment associated to other equipment, by their common RCU
     *
     * @param {string} equipmentId Equipment ID
     * @param {string} equipmentType Equipment Type
     * @returns {Equipment[]} Array of equipment
     */
    getAssociatedEquipment(equipmentId, equipmentType = 'speakers') {
        const eq = this.getEquipment(equipmentId);
        const rcuEquipment = _.get(this.model.byRCU, eq?.rcu, []);
        return _.filter(rcuEquipment, { equipmentType });
    }
}
